import { Box } from '@mui/material'
import orderBy from 'lodash/orderBy'
import { RawTodoRelation, RawTodoResult, isCaseTodo } from './PatientToDoService'
import { buildRelatedComponentForModel, type ModelName } from './relatedViews'

type RelationContent = RawTodoRelation['content_object']

/** Content types the case body surfaces itself, so the generic registry never renders them. */
const SELF_RENDERED = ['careplan', 'chatmessagev2', 'phonecall', 'email'] as const
type SelfRendered = (typeof SELF_RENDERED)[number]

const isSelfRendered = (
  content: RelationContent,
): content is Extract<RelationContent, { content_type: SelfRendered }> =>
  (SELF_RENDERED as readonly string[]).includes(content.content_type)

/**
 * Every relation content type the wire can send, minus the ones above, has to be a model the
 * registry can build — `metadataFor` throws on anything it does not know. Asserting that here makes
 * a newly generated relation type a compile error in this file rather than a crash in the case view.
 */
type Assert<T extends ModelName> = T
type _RegistryCoversEveryRelation = Assert<Exclude<RelationContent['content_type'], SelfRendered>>

/**
 * Renders a case's embedded related content (steerages, linked models, etc.),
 * skipping the content types the case body already surfaces on their own
 * (care plans, chat, phone calls, email).
 */
export const EmbeddedRelations = ({ todo }: { todo: RawTodoResult }) => {
  // Only supported on cases for now - use type predicate for safe narrowing
  if (!isCaseTodo(todo)) {
    return null
  }

  const selectedCase = todo.content_object
  const sortedRelations = orderBy(selectedCase.relations ?? [], ['created_at'], ['desc'])

  return (
    <>
      {sortedRelations.map(relation => {
        const content = relation.content_object
        if (isSelfRendered(content)) return null

        const Component = buildRelatedComponentForModel(content.content_type)
        // A steerage can be linked to multiple cases, and its view needs the active one to navigate
        // from Add Provider to provider search and back. Copied rather than assigned onto the
        // relation, which is React Query's cached object.
        const model =
          content.content_type === 'steerage'
            ? { ...content, active_case_id: selectedCase.id }
            : content

        return (
          <Box key={`${content.content_type}_${relation.object_id}`} sx={{ mt: 4, mb: 4 }}>
            <Component model={model} />
          </Box>
        )
      })}
    </>
  )
}
